import React, { useRef, useState, useCallback, useEffect } from "react";
import SEO from "@/components/SEO";
import {
  organizationSchema,
  websiteSchema,
  faqSchema,
  webAppSchema,
} from "@/lib/schema";
import {
  Zap,
  FileText,
  Eye,
  MessageSquare,
  PenTool,
  ArrowDown,
  Globe,
  Shield,
  Users,
  Sparkles,
  TrendingUp,
  MessageCircle,
  Newspaper,
  Quote,
  Briefcase,
  Cpu,
  Landmark,
  Earth,
  HeartPulse,
  Clapperboard,
  Dumbbell,
  Scale,
  Mail,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { trackEvent, trackExternalLink } from "@/lib/analytics";

const GRID_CELL = 100;
const GRID_COLS = Math.ceil(1920 / GRID_CELL);
const GRID_ROWS = Math.ceil(1080 / GRID_CELL);
const GRID_GLOWS = Array.from({ length: 14 }, (_, i) => ({
  col: Math.floor(Math.random() * GRID_COLS),
  row: Math.floor(Math.random() * GRID_ROWS),
  delay: i * 0.7 + Math.random() * 2,
  duration: 4 + Math.random() * 3,
}));

function AnimatedGrid({ clickGlows = [] }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-0">
      {GRID_GLOWS.map((g, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            left: g.col * GRID_CELL,
            top: g.row * GRID_CELL,
            width: GRID_CELL,
            height: GRID_CELL,
            background: "var(--grid-highlight)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{
            duration: g.duration,
            delay: g.delay,
            repeat: Infinity,
            repeatDelay: 1.5,
            ease: "easeInOut",
            times: [0, 0.2, 0.8, 1],
          }}
        />
      ))}
      {clickGlows.map((g) => (
        <motion.div
          key={g.id}
          className="absolute"
          style={{
            left: g.col * GRID_CELL,
            top: g.row * GRID_CELL,
            width: GRID_CELL,
            height: GRID_CELL,
            background: "var(--grid-highlight)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{
            duration: 1.5,
            ease: "easeInOut",
            times: [0, 0.15, 0.7, 1],
          }}
        />
      ))}
    </div>
  );
}

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const NAV_LINKS = [
  { label: "Features", href: "#offerings" },
  { label: "How it Works", href: "#how-it-works" },
  { label: "Categories", href: "#categories" },
  { label: "Why Zinformed", href: "#why-zinformed" },
];

function LandingHeader({ onGetStarted }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const container = document.getElementById("landing-scroll-container");
    if (!container) return;
    const onScroll = () => setScrolled(container.scrollTop > 20);
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    const container = document.getElementById("landing-scroll-container");
    const header = document.querySelector("header");
    const headerHeight = header ? header.offsetHeight : 0;
    if (el && container) {
      const top =
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop -
        headerHeight;
      container.scrollTo({ top, behavior: "smooth" });
    } else {
      el?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToTop = () => {
    setMobileOpen(false);
    const container = document.getElementById("landing-scroll-container");
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-gray-200/50 bg-white/70 shadow-lg shadow-black/[0.04] backdrop-blur-xl dark:border-white/[0.08] dark:bg-gray-950/70 dark:shadow-black/20"
          : "border-transparent bg-white/50 backdrop-blur-md dark:bg-gray-950/50"
      }`}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:h-16 sm:px-6">
        {/* Logo */}
        <button
          type="button"
          onClick={scrollToTop}
          className="flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 dark:focus-visible:ring-white/40"
          aria-label="Back to top"
        >
          <img
            src="/icon-single-light.png"
            alt="Zinformed logo"
            className="mr-[0.4em] h-[1.5em] w-auto object-cover dark:hidden"
          />
          <img
            src="/icon-single.png"
            alt="Zinformed logo"
            className="mr-[0.4em] hidden h-[1.5em] w-auto object-cover dark:block"
          />
          <span className="text-base font-bold text-gray-900 dark:text-white sm:text-lg">
            informed
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="rounded-md px-3 py-1.5 text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              {link.label}
            </button>
          ))}
          <a
            href="https://x.com/ZinformedonX"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 rounded-md px-3 py-1.5 text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            News
            <ArrowRight className="h-3 w-3 -rotate-45" />
          </a>
        </nav>

        {/* Desktop buttons */}
        <div className="hidden items-center gap-2 lg:flex">
          <button
            onClick={onGetStarted}
            className="rounded-lg bg-gray-900/10 px-4 py-1.5 text-[13px] font-semibold text-gray-900 transition-colors hover:bg-gray-900/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
          >
            Login
          </button>
          <button
            onClick={onGetStarted}
            className="rounded-lg border border-gray-900 bg-gray-900 px-4 py-1.5 text-[13px] font-semibold text-white transition-all hover:bg-gray-800 dark:border-white/30 dark:bg-transparent dark:text-white dark:hover:bg-white dark:hover:text-gray-900"
          >
            Get Started
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="rounded-md p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white lg:hidden"
        >
          {mobileOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-gray-200/50 bg-white/80 px-4 pb-4 pt-2 backdrop-blur-xl dark:border-white/[0.08] dark:bg-gray-950/80 lg:hidden">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="block w-full py-2.5 text-left text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              {link.label}
            </button>
          ))}
          <div className="mt-3 flex flex-col gap-2">
            <button
              onClick={() => {
                setMobileOpen(false);
                onGetStarted();
              }}
              className="rounded-lg bg-gray-900/10 px-4 py-2 text-sm font-semibold text-gray-900 dark:bg-white/10 dark:text-white"
            >
              Login
            </button>
            <button
              onClick={() => {
                setMobileOpen(false);
                onGetStarted();
              }}
              className="rounded-lg border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-semibold text-white dark:border-white/30 dark:bg-transparent dark:text-white"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

const Divider = () => (
  <div className="mx-auto my-0 h-px max-w-5xl bg-gradient-to-r from-transparent via-gray-300 to-transparent dark:via-gray-800" />
);

const offerings = [
  {
    icon: Zap,
    title: "Most important topics. Everyday",
    desc: "Across Finance, Healthcare, Tech, Policy, Culture, and more. Stay informed without the overwhelm. Everything in one place.",
  },
  {
    icon: FileText,
    title: "Clear summaries with deeper context",
    desc: "Multiple perspectives. Understand the issue, not just the headline. Clarity over noise.",
  },
  {
    icon: Eye,
    title: "React and share your view",
    desc: "See where the conversation stands. Engage with clarity, not noise. Ideas first.",
  },
  {
    icon: MessageSquare,
    title: "Ask meaningful questions and invite informed opinions",
    desc: "Start conversations that matter. Your voice matters.",
  },
];

const howItWorksSteps = [
  {
    title: "Topic of the Day",
    description:
      "A curated conversation that matters, selected by Zinformed AI to keep you informed on what\u2019s relevant right now.",
    icon: Zap,
    emphasis: "Fresh & Relevant",
    features: [
      {
        icon: Sparkles,
        title: "AI Curated",
        description:
          "Smart topic selection based on global relevance and trending conversations",
      },
      {
        icon: TrendingUp,
        title: "Daily Fresh",
        description:
          "New perspectives delivered every single day to keep you ahead",
      },
      {
        icon: Globe,
        title: "Trending Now",
        description:
          "Stay informed with topics that matter most around the world",
      },
    ],
  },
  {
    title: "Full Report",
    description:
      "Go beyond the headline. Read summaries, explore context, watch videos - everything you need in one place.",
    icon: FileText,
    emphasis: "Complete Picture",
    features: [
      {
        icon: Eye,
        title: "Summary View",
        description:
          "Get the full story distilled into clear, concise summaries",
      },
      {
        icon: FileText,
        title: "Context Deep-dive",
        description: "Explore background, timeline, and related coverage",
      },
      {
        icon: Newspaper,
        title: "Visual Media",
        description:
          "Watch videos and view infographics for richer understanding",
      },
    ],
  },
  {
    title: "Multiple Perspectives",
    description:
      "See how issues are viewed from different angles supportive, critical, and neutral so you can decide for yourself.",
    icon: Eye,
    emphasis: "360\u00b0 View",
    features: [
      {
        icon: Shield,
        title: "Supportive View",
        description: "Understand the arguments in favor and their reasoning",
      },
      {
        icon: MessageSquare,
        title: "Critical Analysis",
        description: "See the counterpoints and potential concerns raised",
      },
      {
        icon: Users,
        title: "Neutral Ground",
        description: "Find the balanced middle ground with unbiased facts",
      },
    ],
  },
  {
    title: "Influential Voices",
    description:
      "What leaders, experts, and shapers are saying in real time. Quotes and reactions, sourced and presented clearly to add depth to the discussion.",
    icon: Quote,
    emphasis: "Expert Insights",
    features: [
      {
        icon: Users,
        title: "Key Figures",
        description:
          "Curated quotes from leaders, analysts, and domain experts",
      },
      {
        icon: MessageCircle,
        title: "Real-time Reactions",
        description:
          "See how influential voices are responding as events unfold",
      },
      {
        icon: Globe,
        title: "Global Reach",
        description:
          "Perspectives from voices across regions, industries, and ideologies",
      },
    ],
  },
  {
    title: "Add Your Voice",
    description:
      "React to topics, share your opinion, and see where the community discussion stands in real time.",
    icon: MessageSquare,
    emphasis: "Be Heard",
    features: [
      {
        icon: Zap,
        title: "Quick React",
        description: "Share your instant reaction with a single tap",
      },
      {
        icon: MessageCircle,
        title: "Share Opinion",
        description: "Share your point of view and join the conversation",
      },
      {
        icon: TrendingUp,
        title: "Community Pulse",
        description:
          "See how the community feels with real-time sentiment data",
      },
    ],
  },
  {
    title: "Create Posts",
    description:
      "Start your own conversations with AI-powered noise filtering \u2014 only thoughtful, quality posts go live.",
    icon: PenTool,
    emphasis: "Quality First",
    features: [
      {
        icon: Sparkles,
        title: "AI Filtering",
        description: "Smart filters ensure only quality content gets published",
      },
      {
        icon: Shield,
        title: "Quality Control",
        description: "Automated checks maintain high discussion standards",
      },
      {
        icon: ArrowRight,
        title: "Instant Publish",
        description: "Go live immediately once your post meets the quality bar",
      },
    ],
  },
];

const categories = [
  { icon: Briefcase, name: "Economy", desc: "Markets, jobs, policy, growth" },
  {
    icon: Cpu,
    name: "Technology",
    desc: "Innovation, platforms, future shifts",
  },
  {
    icon: Landmark,
    name: "Politics",
    desc: "Governance, elections, institutions",
  },
  { icon: Earth, name: "World", desc: "Global events, diplomacy, relations" },
  { icon: Scale, name: "Society", desc: "Culture, rights, change, justice" },
  { icon: Dumbbell, name: "Sports", desc: "Events, athletes, competition" },
  {
    icon: Clapperboard,
    name: "Entertainment",
    desc: "Movies, music, pop culture",
  },
  {
    icon: HeartPulse,
    name: "Health",
    desc: "Wellness, public health, mind & body",
  },
];

function Hero({ onGetStarted }) {
  return (
    <section className="relative -mt-16 flex min-h-screen items-center justify-center px-4 sm:px-6">
      <div className="w-full max-w-4xl space-y-6 text-center sm:space-y-8">
        <motion.h1
          className="inline-flex items-center justify-center px-4 text-5xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl md:text-7xl lg:text-8xl"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <img
            src="/icon-single-light.png"
            alt="Zinformed logo"
            className="inline-block h-[1em] w-auto object-cover dark:hidden"
          />
          <img
            src="/icon-single.png"
            alt="Zinformed logo"
            className="hidden h-[1em] w-auto object-cover dark:inline-block"
          />
          informed
        </motion.h1>

        <motion.p
          style={{
            marginTop: "0px",
          }}
          className="mx-auto max-w-2xl px-6 text-base leading-relaxed text-gray-500 dark:text-gray-400 sm:text-lg md:text-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          See every side of the story
        </motion.p>

        <motion.p
          style={{
            marginTop: "25px",
          }}
          className="mx-auto max-w-4xl px-6 text-base leading-relaxed text-gray-500 dark:text-gray-400 sm:text-lg md:text-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          In a world full of noise, Zinformed brings clarity, Real-time reports
          on the topics shaping our lives, views from every angle, timely
          insights from those who influence the debate, and room for your own
          thoughtful voice.
        </motion.p>

        <motion.div
          className="flex flex-col items-center gap-3 pt-4 sm:flex-row sm:justify-center sm:gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <Button
            data-testid="get-started-btn"
            onClick={() => {
              trackEvent("landing_cta", {
                button: "get_started",
                section: "hero",
              });
              onGetStarted();
            }}
            className="rounded-full bg-gray-900 px-8 py-5 text-base font-semibold text-white shadow-lg transition-all hover:scale-105 hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-100 sm:px-10 sm:py-6 sm:text-lg"
          >
            Get started
          </Button>
          <button
            onClick={() => {
              trackEvent("landing_cta", {
                button: "explore_now",
                section: "hero",
              });
              document
                .getElementById("offerings")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            Explore now
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </button>
        </motion.div>

        <motion.p
          className="pt-4 text-xs tracking-wide text-gray-400 dark:text-gray-600"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          Balanced views &nbsp;&bull;&nbsp; Thoughtful discussions
        </motion.p>
      </div>
    </section>
  );
}

function Offerings() {
  return (
    <section id="offerings" className="px-4 py-24 sm:px-6 sm:py-32">
      <div className="mx-auto max-w-5xl">
        <FadeIn className="mb-14 text-center">
          <span className="mb-3 inline-block rounded-full bg-blue-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-500 dark:text-blue-400">
            Features
          </span>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
            What Zinformed offers
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-gray-500 dark:text-gray-400">
            Thoughtful tools to understand before you react. Fresh. Relevant.
            Worth your time.
          </p>
        </FadeIn>

        <div className="grid gap-6 sm:grid-cols-2">
          {offerings.map((o, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="group relative h-full overflow-hidden rounded-2xl border border-gray-200 bg-white/70 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-white/[0.06] dark:bg-gray-950/60 sm:p-8">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500 ring-1 ring-blue-500/20 transition-transform duration-300 group-hover:scale-110 dark:text-blue-400">
                  <o.icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                  {o.title}
                </h3>
                <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                  {o.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, index }) {
  const containerRef = useRef(null);

  const themes = [
    {
      gradient: "from-blue-500/20 to-blue-600/5",
      text: "text-blue-500 dark:text-blue-400",
      badge: "bg-blue-500/10 text-blue-400 ring-blue-500/20",
      icon: "from-blue-500/30 to-blue-600/20",
      glow: "bg-blue-500/15",
    },
    {
      gradient: "from-violet-500/20 to-violet-600/5",
      text: "text-violet-500 dark:text-violet-400",
      badge: "bg-violet-500/10 text-violet-400 ring-violet-500/20",
      icon: "from-violet-500/30 to-violet-600/20",
      glow: "bg-violet-500/15",
    },
    {
      gradient: "from-cyan-500/20 to-cyan-600/5",
      text: "text-cyan-500 dark:text-cyan-400",
      badge: "bg-cyan-500/10 text-cyan-400 ring-cyan-500/20",
      icon: "from-cyan-500/30 to-cyan-600/20",
      glow: "bg-cyan-500/15",
    },
    {
      gradient: "from-amber-500/20 to-amber-600/5",
      text: "text-amber-500 dark:text-amber-400",
      badge: "bg-amber-500/10 text-amber-400 ring-amber-500/20",
      icon: "from-amber-500/30 to-amber-600/20",
      glow: "bg-amber-500/15",
    },
    {
      gradient: "from-teal-500/20 to-teal-600/5",
      text: "text-teal-500 dark:text-teal-400",
      badge: "bg-teal-500/10 text-teal-400 ring-teal-500/20",
      icon: "from-teal-500/30 to-teal-600/20",
      glow: "bg-teal-500/15",
    },
    {
      gradient: "from-emerald-500/20 to-emerald-600/5",
      text: "text-emerald-500 dark:text-emerald-400",
      badge: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/20",
      icon: "from-emerald-500/30 to-emerald-600/20",
      glow: "bg-emerald-500/15",
    },
  ];

  const theme = themes[index % themes.length];
  const IconComponent = step.icon;

  return (
    <div ref={containerRef} className="relative">
      <motion.div
        className="relative flex items-center overflow-hidden py-20 sm:py-28 md:py-32"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <div
          className={`pointer-events-none absolute -left-32 top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full ${theme.glow} blur-[150px]`}
        />
        <div
          className={`pointer-events-none absolute -bottom-32 -right-32 h-[300px] w-[300px] rounded-full ${theme.glow} opacity-50 blur-[100px]`}
        />

        <div className="relative mx-auto w-full max-w-7xl px-6 sm:px-8 lg:px-12">
          <div
            className="pointer-events-none absolute -right-6 top-1/2 -translate-y-1/2 select-none opacity-[0.04] dark:opacity-[0.08] sm:-right-10"
            aria-hidden="true"
          >
            <IconComponent
              className={`h-[180px] w-[180px] sm:h-[260px] sm:w-[260px] lg:h-[340px] lg:w-[340px] ${theme.text}`}
            />
          </div>

          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="relative z-10 space-y-6">
              <h3 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl md:text-5xl lg:text-6xl">
                {step.title}
              </h3>

              <p className="max-w-md text-base leading-relaxed text-gray-500 dark:text-gray-400 sm:text-lg">
                {step.description}
              </p>

              <div
                className={`inline-flex items-center gap-2 text-sm font-semibold ${theme.text}`}
              >
                <ArrowRight className="h-4 w-4" />
                {step.emphasis}
              </div>
            </div>
            <div className="relative z-10 space-y-4">
              {step.features.map((feature, fi) => (
                <motion.div
                  key={fi}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: fi * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-2xl border from-gray-950/80 to-gray-950/80 p-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 dark:border-white/[0.06] dark:bg-gradient-to-br dark:hover:border-white/10 sm:p-6"
                >
                  <div
                    className={`pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br ${theme.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                  />
                  <div className="relative z-10 flex items-start gap-4">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${theme.icon} ring-1 ring-black/5 dark:ring-white/10`}
                    >
                      <feature.icon className={`h-5 w-5 ${theme.text}`} />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                        {feature.title}
                      </h4>
                      <p className="mt-1 text-xs leading-relaxed text-gray-500 sm:text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function HowItWorks({ onGetStarted }) {
  return (
    <section id="how-it-works" className="relative">
      <div className="relative px-4 text-center">
        <span className="mb-4 mt-3 inline-block rounded-full bg-purple-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-purple-500 dark:text-purple-400">
          How It Works
        </span>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
          Your Journey Through Zinformed
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-sm text-gray-500 dark:text-gray-500 sm:text-base">
          Six steps from headlines to real understanding
        </p>
      </div>

      {howItWorksSteps.map((step, i) => (
        <StepCard key={i} step={step} index={i} />
      ))}

      <FadeIn className="pb-12 pt-4 text-center">
        <Button
          onClick={() => {
            trackEvent("landing_cta", {
              button: "join_discussion",
              section: "how_it_works",
            });
            onGetStarted();
          }}
          className="rounded-full bg-gray-900 px-8 py-5 text-base font-semibold text-white shadow-lg transition-all hover:scale-105 hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-100 sm:px-10 sm:py-6 sm:text-lg"
        >
          Join the Discussion
        </Button>
      </FadeIn>
    </section>
  );
}

function Categories() {
  return (
    <section id="categories" className="px-4 py-24 sm:px-6 sm:py-32">
      <div className="mx-auto max-w-5xl">
        <FadeIn className="mb-14 text-center">
          <span className="mb-3 inline-block rounded-full bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-500 dark:text-emerald-400">
            Topics
          </span>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
            Choose what matters to you
          </h2>
        </FadeIn>

        <div className="grid grid-cols-2 gap-5 sm:gap-6 lg:grid-cols-4">
          {categories.map((cat, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <div className="group relative flex h-full flex-col items-center rounded-2xl border border-gray-200 bg-white/70 p-7 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-white/[0.06] dark:bg-gray-950/60 sm:p-9">
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition-transform duration-300 group-hover:scale-110 dark:bg-white/[0.06] dark:text-gray-300">
                  <cat.icon className="h-8 w-8" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">
                  {cat.name}
                </h3>
                <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-500">
                  {cat.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyPerspective({ onGetStarted }) {
  return (
    <section id="why-zinformed" className="px-4 py-24 sm:px-6 sm:py-32">
      <div className="mx-auto max-w-3xl">
        <FadeIn className="text-center">
          <span className="mb-3 inline-block rounded-full bg-amber-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-amber-500 dark:text-amber-400">
            Our Story
          </span>
          <h2 className="mb-8 text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
            Why Zinformed?
          </h2>
        </FadeIn>

        <FadeIn delay={0.15}>
          <div className="space-y-5 text-center text-base leading-relaxed text-gray-600 dark:text-gray-400 sm:text-lg">
            <p>
              Zinformed was born out of frustration with endless noise in
              today&apos;s information flood. Headlines scream, feeds overwhelm,
              and real understanding gets lost.
            </p>
            <p>
              We built a platform that helps cut through the chaos: surfacing
              the conversations that truly matter, presenting views from every
              angle, capturing what influential voices are saying right now, and
              opening space for thoughtful discussion without distraction or
              division.
            </p>
            <p>
              Our goal is simple:{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                Help you stay informed, see multiple sides, and add your voice
                confidently.
              </span>{" "}
              Whether you&apos;re debating cricket ethics, economic inequality,
              AI&apos;s future, or global tensions Zinformed keeps the signal
              sharp and makes every conversation count.
            </p>
            <p className="text-gray-500">
              Sign up free and start exploring balanced views and real
              discussions today.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.3} className="mt-10 text-center">
          <Button
            onClick={() => {
              trackEvent("landing_cta", {
                button: "get_started_now",
                section: "why_zinformed",
              });
              onGetStarted();
            }}
            className="rounded-full bg-gray-900 px-8 py-5 text-base font-semibold text-white shadow-lg transition-all hover:scale-105 hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-100 sm:px-10 sm:py-6 sm:text-lg"
          >
            Get Started Now
          </Button>
        </FadeIn>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-200 px-4 py-12 dark:border-gray-900 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <div className="flex items-center">
            <img
              src="/icon-single-light.png"
              alt="Zinformed"
              className="mr-[0.4em] h-[1.5em] rounded-lg object-cover dark:hidden"
            />
            <img
              src="/icon-single.png"
              alt="Zinformed"
              className="mr-[0.4em] hidden h-[1.5em] rounded-lg object-cover dark:block"
            />
            <span className="text-base font-bold text-gray-900 dark:text-white">
              informed
            </span>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 dark:text-gray-400"></div>
        </div>

        <div className="flex flex-col items-center gap-4 text-sm text-gray-400 dark:text-gray-500 sm:flex-row sm:justify-between md:mt-6">
          <div className="flex items-center gap-1.5">
            <Mail className="h-4 w-4" />
            <a
              href="mailto:contact@zinformed.com"
              className="transition-colors hover:text-gray-900 dark:hover:text-white"
              onClick={() =>
                trackEvent("contact_click", {
                  method: "email",
                  section: "footer",
                })
              }
            >
              contact@zinformed.com
            </a>
          </div>

          <div className="flex items-center gap-5">
            <a
              href="https://x.com/ZinformedonX"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-gray-900 dark:hover:text-white"
              aria-label="X (Twitter)"
              onClick={() =>
                trackExternalLink("https://x.com/ZinformedonX", "footer")
              }
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M18.25 3H21l-6.74 7.7L22 21h-6.4l-4.1-5.36L6.94 21H4.2l7.25-8.3L2 3h6.56l3.7 4.84L18.25 3Zm-1.12 16h1.55L7.05 5H5.46l11.67 14Z" />
              </svg>
            </a>
            <a
              href="https://instagram.com/zinformed"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-gray-900 dark:hover:text-white"
              aria-label="Instagram"
              onClick={() =>
                trackExternalLink("https://instagram.com/zinformed", "footer")
              }
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <circle cx="12" cy="12" r="5" />
                <circle
                  cx="17.5"
                  cy="6.5"
                  r="1.5"
                  fill="currentColor"
                  stroke="none"
                />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/zinformed"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-gray-900 dark:hover:text-white"
              aria-label="LinkedIn"
              onClick={() =>
                trackExternalLink(
                  "https://www.linkedin.com/company/zinformed",
                  "footer",
                )
              }
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-gray-400 dark:text-gray-600">
          &copy; 2026 Zinformed &ndash; All rights reserved
        </p>
      </div>
    </footer>
  );
}

const LandingPage = ({ onGetStarted }) => {
  const [clickGlows, setClickGlows] = useState([]);
  const sectionsTracked = useRef(new Set());

  useEffect(() => {
    const sections = document.querySelectorAll(
      "section[id], section:not([id])",
    );
    const sectionNames = [
      "hero",
      "offerings",
      "how_it_works",
      "categories",
      "why_zinformed",
    ];
    let idx = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const name =
              entry.target.dataset.section ||
              entry.target.id ||
              `section_${idx}`;
            if (!sectionsTracked.current.has(name)) {
              sectionsTracked.current.add(name);
              trackEvent("landing_section_view", { section: name });
            }
          }
        });
      },
      { threshold: 0.3 },
    );

    sections.forEach((s, i) => {
      s.dataset.section = s.id || sectionNames[i] || `section_${i}`;
      observer.observe(s);
    });

    return () => observer.disconnect();
  }, []);

  const handlePageClick = useCallback((e) => {
    const col = Math.floor(e.clientX / GRID_CELL);
    const row = Math.floor(e.clientY / GRID_CELL);
    const id = Date.now() + Math.random();
    setClickGlows((prev) => [...prev, { id, col, row }]);
    setTimeout(() => {
      setClickGlows((prev) => prev.filter((g) => g.id !== id));
    }, 1500);
  }, []);

  return (
    <>
      <SEO
        title="See Both Sides of Every Story"
        description="Zinformed shows both the positive and critical sides of every news story. No bias, no echo chambers — just clear, balanced news. Read, discuss, and stay truly informed."
        canonical="/"
        keywords="zinformed, zinformed news, zinformed app, zinformed.com, balanced news, unbiased news, news analysis, both sides of the story, multiple viewpoints, news perspectives, current affairs, india news, ai news, news aggregator, informed opinions"
        schema={[
          organizationSchema(),
          websiteSchema(),
          webAppSchema(),
          faqSchema([
            {
              question: "What is Zinformed?",
              answer:
                "Zinformed is an AI-powered news platform that shows both the positive and critical sides of every story, helping you form balanced, informed opinions without bias or echo chambers.",
            },
            {
              question: "Is Zinformed free to use?",
              answer:
                "Yes, Zinformed is completely free. You can read news, explore multiple perspectives, and join discussions at no cost.",
            },
            {
              question: "How does Zinformed ensure unbiased news?",
              answer:
                "Zinformed aggregates multiple sources and uses AI to present both supporting and opposing viewpoints for every story, so you can read, compare, and decide for yourself.",
            },
            {
              question: "Can I share my opinion on news stories?",
              answer:
                "Yes, you can join community discussions and share your point of view on any news story through Zinformed’s community features.",
            },
            {
              question: "How can I contact Zinformed?",
              answer:
                "You can reach the Zinformed team at contact@zinformed.com or follow us on Instagram (@zinformed), X (@ZinformedonX), and LinkedIn (Zinformed App).",
            },
          ]),
        ]}
      />
      <div
        id="landing-scroll-container"
        className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-white dark:bg-black"
        onClick={handlePageClick}
        style={{
          backgroundImage:
            "linear-gradient(to right, var(--grid-color) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-color) 1px, transparent 1px)",
          backgroundSize: `${GRID_CELL}px ${GRID_CELL}px`,
        }}
      >
        <AnimatedGrid clickGlows={clickGlows} />

        <div className="relative z-10">
          <LandingHeader onGetStarted={onGetStarted} />
          <Hero onGetStarted={onGetStarted} />
          <Divider />
          <Offerings />
          <Divider />
          <HowItWorks onGetStarted={onGetStarted} />
          <Divider />
          <Categories />
          <Divider />
          <WhyPerspective onGetStarted={onGetStarted} />
          <Footer />
        </div>
      </div>
    </>
  );
};

export default LandingPage;
